/* ============================================================
   cart-render.js — what the page does about a cart response.

   Split out of cart.js in Phase 8d. The two halves answer different
   questions: cart.js is what the shopper did (POST /cart/add, /cart/change,
   /cart/update and the delegated listeners behind them), this file is what
   the page does with what comes back — swap the rendered regions in, adopt
   their stylesheet, announce the change, put focus back, show the toast.

   Splitting it was forced rather than chosen. `shopify theme check`'s
   AssetSizeJavaScript compares RAW file bytes against .theme-check.yml's
   threshold, despite shopify.dev documenting that setting as a compressed
   size — see tools/audit.mjs §7 for the experiment that settled it. cart.js
   had 570 bytes of margin under the threshold in force at the time and the
   double-add fix needed 655. That threshold is a backstop now and the binding
   per-file figure is a different measurement; the split stands on its own.
   The seam is real, though, and is the same one nmp.js already draws when it
   exports NmpAdoptSectionStyles for exactly this reason.

   Loaded before cart.js in layout/theme.liquid. Both are defer, so document
   order is execution order; cart.js also guards on this file's export and
   goes inert without it, which leaves the native form posts working.

   No state of its own beyond the toast timer, and no model of the cart —
   every number on screen came from Liquid, which is the rule cart.js's own
   header states and the reason there is nothing here to drift.

   Dependencies: assets/nmp.js (window.NmpAdoptSectionStyles, optional).
   Related docs: docs/01 §Cart mutations.
   Last updated: 2026-08-02
   ============================================================ */
(function () {
  'use strict';

  var TOAST_DWELL = 5000;
  var toastTimer = null;

  /* Byte-identical to cart.js's copy. Three lines with no module system to
     share them through is cheaper than a fourth file, and Phase 7 measured
     hoisting helpers like this one at break-even once the helper's own
     comment is counted. */
  function all(selector) {
    return Array.prototype.slice.call(document.querySelectorAll(selector));
  }

  /* ------------------------------------------------------------- swap */

  /* Three rules. Nothing above [data-cart-root] is touched, so the drawer
     keeps its open state and the element focus returns to, and the section
     wrapper the editor binds to is never replaced — the rule collection.js
     states at line 227. */
  function applySections(sections) {
    if (!sections) return;
    var parser = new DOMParser();

    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (typeof html !== 'string') return;
      var doc = parser.parseFromString(html, 'text/html');

      /* Part of a response means its stylesheet by hand: Shopify ships a page
         only the CSS of the files it rendered, and a bag empty on load never
         rendered a line. See assets/nmp.js.

         BEFORE the insertions below. Adopting after still ends up styled, but
         leaves the browser a frame to paint the new markup with no rules for
         it — the first line added to an empty bag flashed every time. Rule 4e
         in tools/audit.mjs now checks the order, not just the call. */
      if (window.NmpAdoptSectionStyles) window.NmpAdoptSectionStyles(doc, id);

      /* 1. the drawer's contents */
      var root = doc.querySelector('[data-cart-root]');
      var liveRoot = document.querySelector('[data-cart-root]');
      if (root && liveRoot) liveRoot.innerHTML = root.innerHTML;

      /* 2. the bag count, wherever it appears. Never the header's own
            innerHTML — that would close an open mega panel and re-run the
            --header-h measurement for one number. */
      var count = doc.querySelector('[data-cart-count]');
      if (count) {
        all('[data-cart-count]').forEach(function (el) {
          el.innerHTML = count.innerHTML;
        });
      }

      /* 3. a whole section region, but only where the live one asked for it.
            The header also carries id="section-<id>", and replacing that is
            exactly what rule 2 exists to avoid. */
      var live = document.getElementById('section-' + id);
      if (live && live.hasAttribute('data-cart-results')) {
        var incoming = doc.getElementById('section-' + id);
        if (incoming) live.innerHTML = incoming.innerHTML;
      }
    });
  }

  /* --------------------------------------------------------- announce */

  /* Written synchronously, and never emptied first. A live region only speaks
     when its content changes, so the same message twice running would be
     silent — hence the trailing space, which changes the text without changing
     a word of it. Emptying it instead would be the obvious trick and is the
     wrong one: the status line is visually hidden, an empty one has no box at
     all, and an element with no box cannot be focused, which is exactly what
     restore() needs to do with it when the last line is removed. */
  function announce(message) {
    if (!message) return;
    all('[data-cart-status]').forEach(function (el) {
      el.textContent = el.textContent.trim() === message ? message + ' ' : message;
    });
  }

  /* ------------------------------------------------------------ focus */

  /* The drawer and the cart page show the same bag, so on /cart every line key
     exists twice over — and the drawer renders first, which makes it what a
     document-wide lookup finds. Remembering which of the two was being used is
     the whole reason this returns a scope: without it, using the cart page
     hands focus to a button inside the closed drawer, where it silently dies. */
  function scopeOf(el) {
    if (el.closest('[data-cart-root]')) return 'drawer';
    var region = el.closest('[data-cart-results][data-section-id]');
    return region ? region.getAttribute('data-section-id') : null;
  }

  function capture() {
    var active = document.activeElement;
    if (!active || !active.closest) return null;
    var line = active.closest('[data-cart-line]');
    if (!line) return null;
    var step = active.closest('[data-cart-step]');
    return {
      key: line.getAttribute('data-line-key'),
      variant: line.getAttribute('data-line-variant'),
      control: step ? step.getAttribute('data-cart-step') : 'remove',
      scope: scopeOf(line)
    };
  }

  function restore(state) {
    if (!state) return;

    /* Re-found rather than remembered: whichever region it was, the swap has
       just replaced everything inside it. */
    var scope = null;
    if (state.scope === 'drawer') scope = document.querySelector('[data-cart-root]');
    else if (state.scope) {
      scope = document.querySelector('[data-cart-results][data-section-id="' + state.scope + '"]');
    }
    if (!scope) scope = document;

    var line = null;
    if (state.key) line = scope.querySelector('[data-cart-line][data-line-key="' + state.key + '"]');
    /* A line key is not permanent — Shopify rewrites it when a property or a
       discount changes, which crossing a cart-level discount threshold does to
       every line at once. The variant survives. */
    if (!line && state.variant) {
      line = scope.querySelector('[data-cart-line][data-line-variant="' + state.variant + '"]');
    }

    var target = null;
    if (line) {
      target =
        state.control === 'remove'
          ? line.querySelector('[data-cart-remove]')
          : line.querySelector('[data-cart-step="' + state.control + '"]');
    }
    /* The line is gone, which is what was asked for. Take the next one. */
    if (!target) target = scope.querySelector('[data-cart-line] [data-cart-remove]');
    /* Nothing left. The status line is the shortest honest answer to "what
       just happened", and it is a live region. There can be two of them on
       the cart page — the drawer's and the page's — so the visible one wins;
       the test is on the panel it sits in, because the line itself is
       visually hidden and measures nothing either way. */
    if (!target) {
      var statuses = all('[data-cart-status]');
      target =
        statuses.filter(function (el) {
          var host = el.parentElement;
          return host && (host.offsetParent !== null || host.getClientRects().length > 0);
        })[0] || statuses[0];
    }
    if (target) target.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------ toast */

  function hideToast() {
    var toast = document.querySelector('[data-cart-toast]');
    if (toast) toast.hidden = true;
    window.clearTimeout(toastTimer);
    toastTimer = null;
  }

  function showToast() {
    var toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, TOAST_DWELL);
  }

  window.NmpCartRender = {
    apply: applySections,
    announce: announce,
    capture: capture,
    restore: restore,
    showToast: showToast,
    hideToast: hideToast
  };
})();
